import React, { useMemo } from "react";
import { Button, Chip, CircularProgress, IconButton, Tooltip } from "@mui/joy";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import { type Components } from "react-markdown";
import { defaultSchema } from "rehype-sanitize";
import { QCFinding, FindingSeverity } from "../../types/qcTypes";
import {
  QC_ATTR_FINDING_ID,
  QC_ATTR_KIND,
  QC_KIND_ACTIONS,
  QC_KIND_ORIGINAL,
  QC_KIND_SUGGESTED,
} from "./qcRehypePlugin";

// Shared building blocks for the two QC findings renderers:
//   - IndependentQCResultsView (independent QC, markdown content)
//   - PanelQCResultsView       (integrated/quick_action, HTML *or* markdown)
// Only the *content parsing* differs between modes (remark vs rehype-parse).
// The redline span UI (QcSpan), heading handling, severity helpers, sanitize
// schema, and the HTML/markdown format detector are identical and live here.

// --- Severity helpers ---

export const severityColor: Record<FindingSeverity, string> = {
  critical: "danger",
  major: "warning",
  minor: "neutral",
  info: "primary",
};

export const severityLabel: Record<FindingSeverity, string> = {
  critical: "Critical",
  major: "Major",
  minor: "Minor",
  info: "Info",
};

// --- Sanitize schema: allow data-qc-* on span (used by both content renderers,
// after parsing and before rehypeQcFindings adds the redline spans) ---

export const qcSanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    span: [
      ...(defaultSchema.attributes?.span || []),
      QC_ATTR_FINDING_ID,
      QC_ATTR_KIND,
    ],
  },
};

export type FindingActionType =
  "accept" | "decline" | "revert" | "feedback-up" | "feedback-down";

// Detect whether section content is HTML or markdown. Ported from the editor's
// own branch (GenerationView.tsx isHtmlContent) so QC renders content exactly
// the way the editor does: has HTML tags and no markdown patterns -> HTML.
export const isHtmlContent = (content: string): boolean => {
  if (!content) return false;
  const hasHtmlTags = /<[^>]+>/.test(content);
  const markdownPatterns = [
    /^#{1,6}\s/m, // headers
    /^\*\s/m, // bullets
    /^\d+\.\s/m, // numbered lists
    /\[.*\]\(.*\)/, // links
    /\*\*.*\*\*/, // bold
    /`.*`/, // code
    /^\s*\|.*\|.*\|/m, // tables
  ];
  const hasMarkdownPatterns = markdownPatterns.some((p) => p.test(content));
  return hasHtmlTags && !hasMarkdownPatterns;
};

// --- Shared components map (QcSpan + heading handling) ---

export interface QcComponentsParams {
  // The owning mode component's CSS module. Both modules define the same
  // class names (findingOriginal, findingSuggested, findingActions, ...).
  styles: Record<string, string>;
  findingsById: Map<string, QCFinding>;
  readOnly: boolean;
  // When true, render headings at native level. The panel flows downgrade
  // h1-h3 -> h4 etc. because the panel header already shows the section title;
  // the document flow (IndependentQCDocumentView) preserves them.
  preserveHeadings: boolean;
  // Value included in the memo dependency list so the span re-renders its
  // per-finding loading spinners when an action starts/finishes.
  loadingAction: { findingId: number; action: FindingActionType } | null;
  isLoading: (findingId: number, action: FindingActionType) => boolean;
  isAnyLoadingFor: (findingId: number) => boolean;
  onAccept: (findingId: number) => void;
  onDecline: (findingId: number) => void;
  onOpenEdit: (e: React.MouseEvent<HTMLElement>, finding: QCFinding) => void;
  onDetails: (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    finding: QCFinding,
  ) => void;
  onRevert: (findingId: number) => void;
  onRevertFinding?: (findingId: number) => void | Promise<void>;
}

// Inline action buttons: at least 24px so they meet the minimum target size.
const actionButtonSx = { minWidth: 24, minHeight: 24, p: 0.25 } as const;
const actionIconSx = { fontSize: 16 } as const;
const actionSpinnerSx = { "--CircularProgress-size": "16px" } as const;

// Builds the components map consumed by both <ReactMarkdown> (markdown branch)
// and hast-util-to-jsx-runtime (HTML branch). `span` detects the data-qc-*
// markers emitted by rehypeQcFindings and renders the inline redline UI.
export function useQcComponents(params: QcComponentsParams): Components {
  const {
    styles,
    findingsById,
    readOnly,
    preserveHeadings,
    loadingAction,
    isLoading,
    isAnyLoadingFor,
    onAccept,
    onDecline,
    onOpenEdit,
    onDetails,
    onRevert,
    onRevertFinding,
  } = params;

  return useMemo<Components>(() => {
    type SpanProps = React.HTMLAttributes<HTMLSpanElement> &
      Record<string, unknown>;

    const QcSpan: React.FC<SpanProps> = (props) => {
      const findingIdRaw =
        (props["data-qc-finding-id"] as string | undefined) ?? undefined;
      const kind = (props["data-qc-kind"] as string | undefined) ?? undefined;
      const { children, ...rest } = props;

      if (!findingIdRaw || !kind) {
        return (
          <span {...(rest as React.HTMLAttributes<HTMLSpanElement>)}>
            {children as React.ReactNode}
          </span>
        );
      }

      const finding = findingsById.get(findingIdRaw);
      if (!finding) {
        // Stale marker -- render contents as-is, dropping the data attrs.
        return <>{children as React.ReactNode}</>;
      }

      const isAccepted = finding.Resolution === "accepted";
      const isDeclined = finding.Resolution === "declined";
      const isPending = finding.Resolution === null;

      if (kind === QC_KIND_ORIGINAL) {
        return (
          <span
            className={`${styles.findingOriginal} ${
              styles[`severity_${finding.Severity}`] ?? ""
            } ${isAccepted ? styles.findingAccepted : ""} ${
              isDeclined ? styles.findingDeclined : ""
            }`}
            role="button"
            tabIndex={0}
            // The quoted text may end a sentence; don't add a second stop.
            aria-label={`${severityLabel[finding.Severity]} finding: ${(
              finding.OriginalText ?? ""
            ).replace(/[.!?]+\s*$/, "")}. Show details`}
            onClick={(e) => onDetails(e, finding)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onDetails(e, finding);
              }
            }}
          >
            {children as React.ReactNode}
          </span>
        );
      }

      if (kind === QC_KIND_SUGGESTED) {
        return (
          <span
            className={`${styles.findingSuggested} ${
              isAccepted ? styles.findingSuggestedAccepted : ""
            } ${isDeclined ? styles.findingSuggestedDeclined : ""}`}
          >
            {children as React.ReactNode}
          </span>
        );
      }

      if (kind === QC_KIND_ACTIONS) {
        if (readOnly) return null;

        if (isPending) {
          return (
            <span className={styles.findingActions}>
              <Chip
                size="sm"
                variant="soft"
                color={severityColor[finding.Severity] as "danger"}
              >
                {severityLabel[finding.Severity]}
              </Chip>
              <Tooltip title="Accept">
                <IconButton
                  size="sm"
                  variant="plain"
                  color="success"
                  aria-label="Accept"
                  disabled={isAnyLoadingFor(finding.FindingID)}
                  onClick={() => onAccept(finding.FindingID)}
                  sx={actionButtonSx}
                >
                  {isLoading(finding.FindingID, "accept") ? (
                    <CircularProgress size="sm" sx={actionSpinnerSx} />
                  ) : (
                    <CheckIcon sx={actionIconSx} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Decline">
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  aria-label="Decline"
                  disabled={isAnyLoadingFor(finding.FindingID)}
                  onClick={() => onDecline(finding.FindingID)}
                  sx={actionButtonSx}
                >
                  {isLoading(finding.FindingID, "decline") ? (
                    <CircularProgress size="sm" sx={actionSpinnerSx} />
                  ) : (
                    <CloseIcon sx={actionIconSx} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  finding.SuggestedText
                    ? "Edit suggestion"
                    : "Write replacement"
                }
              >
                <IconButton
                  size="sm"
                  variant="plain"
                  color="primary"
                  aria-label={
                    finding.SuggestedText
                      ? "Edit suggestion"
                      : "Write replacement"
                  }
                  disabled={isAnyLoadingFor(finding.FindingID)}
                  onClick={(e) => onOpenEdit(e, finding)}
                  sx={actionButtonSx}
                >
                  <EditIcon sx={actionIconSx} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Details">
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  aria-label="Details"
                  onClick={(e) => onDetails(e, finding)}
                  sx={actionButtonSx}
                >
                  <InfoOutlinedIcon sx={actionIconSx} />
                </IconButton>
              </Tooltip>
            </span>
          );
        }

        // Resolved: helpful feedback prompt + revert.
        return (
          <span className={styles.findingActions}>
            {finding.EditedText != null && (
              <Tooltip
                title={
                  finding.EditedText.length === 0
                    ? "You accepted this as a deletion (original text will be removed)"
                    : "You edited the AI suggestion before accepting"
                }
              >
                <Chip
                  size="sm"
                  variant="soft"
                  color={finding.EditedText.length === 0 ? "danger" : "primary"}
                >
                  {finding.EditedText.length === 0 ? "deleted" : "edited"}
                </Chip>
              </Tooltip>
            )}
            {onRevertFinding && (
              <Tooltip title="Revert this decision">
                <Button
                  size="sm"
                  variant="plain"
                  color="neutral"
                  loading={isLoading(finding.FindingID, "revert")}
                  disabled={isAnyLoadingFor(finding.FindingID)}
                  startDecorator={<UndoIcon sx={actionIconSx} />}
                  onClick={() => onRevert(finding.FindingID)}
                  sx={{ minHeight: 24, px: 0.5 }}
                >
                  Revert
                </Button>
              </Tooltip>
            )}
          </span>
        );
      }

      return (
        <span {...(rest as React.HTMLAttributes<HTMLSpanElement>)}>
          {children as React.ReactNode}
        </span>
      );
    };

    // Downgrade headings by default (panel mode): the section panel already
    // shows the section title in its header bar, so render h1-h3 as h4, h4 as
    // h5, and h5/h6 as bold inline text. In document mode (`preserveHeadings`)
    // the merged content carries the real hierarchy -- keep native levels.
    const result: Components = preserveHeadings
      ? { span: QcSpan as Components["span"] }
      : {
          span: QcSpan as Components["span"],
          h1: ({ node: _n, children, ...rest }) => (
            <h4 {...rest}>{children}</h4>
          ),
          h2: ({ node: _n, children, ...rest }) => (
            <h4 {...rest}>{children}</h4>
          ),
          h3: ({ node: _n, children, ...rest }) => (
            <h4 {...rest}>{children}</h4>
          ),
          h4: ({ node: _n, children, ...rest }) => (
            <h5 {...rest}>{children}</h5>
          ),
          h5: ({ node: _n, children, ...rest }) => (
            <strong {...rest}>{children}</strong>
          ),
          h6: ({ node: _n, children, ...rest }) => (
            <strong {...rest}>{children}</strong>
          ),
        };
    return result;
    // Handlers are intentionally omitted from deps (recaptured when loadingAction
    // changes), mirroring the original single-file renderer's memoization.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    styles,
    findingsById,
    loadingAction,
    readOnly,
    onRevertFinding,
    preserveHeadings,
  ]);
}
